import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { amt, bal, fmtDate, LedgerStatement, money, typeInfo } from '../accounting.api';

const num = { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } as const;
const bold = { ...num, fontWeight: 700 } as const;

/**
 * A ledger's statement in Tally's layout: opening balance, each posting ("To" the ledgers
 * credited against a debit, "By" those debited against a credit) with the running balance,
 * the period's totals and the closing balance. A row opens its voucher.
 */
export default function LedgerStatementTable({ statement: s, onVoucher }: { statement: LedgerStatement; onVoucher?: (id: number) => void }) {
  return (
    <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>Date</TableCell>
            <TableCell>Particulars</TableCell>
            <TableCell>Voucher</TableCell>
            <TableCell align='right'>Debit</TableCell>
            <TableCell align='right'>Credit</TableCell>
            <TableCell align='right'>Balance</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(s.from)}</TableCell>
            <TableCell {...({ colSpan: 4 } as object)} sx={{ fontWeight: 600 }}>
              Opening balance
            </TableCell>
            <TableCell align='right' sx={bold}>
              {bal(s.opening)}
            </TableCell>
          </TableRow>
          {s.entries.length === 0 && (
            <TableRow>
              <TableCell {...({ colSpan: 6 } as object)}>
                <Typography variant='body2' color='text.secondary'>
                  No transactions in this period.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {s.entries.map((e, i) => {
            const debit = Number(e.debit) > 0;
            return (
              <TableRow
                key={`${e.voucherId}-${i}`}
                hover={!!onVoucher}
                onClick={onVoucher ? () => onVoucher(e.voucherId) : undefined}
                sx={{ cursor: onVoucher ? 'pointer' : undefined }}
              >
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(e.date)}</TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <Typography variant='body2'>
                    <Typography component='span' variant='body2' color='text.secondary'>
                      {debit ? 'To ' : 'By '}
                    </Typography>
                    {e.particulars}
                  </Typography>
                  {e.narration && (
                    <Typography variant='caption' color='text.secondary'>
                      {e.narration}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant='body2'>{e.voucherNo}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {typeInfo(e.voucherType).label}
                  </Typography>
                </TableCell>
                <TableCell align='right' sx={num}>
                  {amt(e.debit)}
                </TableCell>
                <TableCell align='right' sx={num}>
                  {amt(e.credit)}
                </TableCell>
                <TableCell align='right' sx={num}>
                  {bal(e.balance)}
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell />
            <TableCell {...({ colSpan: 2 } as object)} sx={{ fontWeight: 700 }}>
              Total
            </TableCell>
            <TableCell align='right' sx={bold}>
              {money(s.totalDebit)}
            </TableCell>
            <TableCell align='right' sx={bold}>
              {money(s.totalCredit)}
            </TableCell>
            <TableCell />
          </TableRow>
          <TableRow sx={{ bgcolor: 'grey.100' }}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(s.to)}</TableCell>
            <TableCell {...({ colSpan: 4 } as object)} sx={{ fontWeight: 700 }}>
              Closing balance
            </TableCell>
            <TableCell align='right' sx={bold}>
              {bal(s.closing)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
